import { INVENTORY_ROUTES } from '../../../config/api-routes';
import instance from '../../../config/instance';

/**
 * Get inventory list with optimized pagination
 * This API fetches inventory items with advanced filtering and pagination
 * Replaces the Redux getInventoryListOptimizedApi action
 *
 * @param {object} params - Query parameters
 * @param {string} params.inventoryType - Type of inventory (managed/unmanaged)
 * @param {boolean} params.pagination - Enable pagination
 * @param {number} params.limit - Number of items per page (default: 25)
 * @param {number} params.page - Page number (default: 1)
 * @param {string} params.search - Search term
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortType - Sort order (ASC/DESC)
 * @param {array} params.partsCategory - Array of parts category filters
 * @param {array} params.partsBrand - Array of parts brand filters
 * @param {array} params.supplier - Array of supplier filters
 * @param {array} params.tyreDimension - Array of tyre dimension filters
 * @param {array} params.tyreModel - Array of tyre model filters
 * @returns {Promise} - Inventory data with pagination info
 */
export const getInventoryListOptimizedApi = async (params = {}) => {
    const queryParams = new URLSearchParams();

    // Add basic parameters to query string
    Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
            // Handle array parameters (filters)
            if (Array.isArray(params[key])) {
                params[key].forEach((item) => {
                    if (item !== undefined && item !== null && item !== '') {
                        queryParams.append(key, item);
                    }
                });
            } else {
                queryParams.append(key, params[key]);
            }
        }
    });

    // Handle specific array filters if passed separately (backward compatibility)
    const { partsCategory, partsBrand, supplier, tyreDimension, tyreModel, ...otherParams } = params;

    // Add array filters if they exist
    if (partsCategory && Array.isArray(partsCategory)) {
        partsCategory.forEach((item) => queryParams.append('partsCategory', item));
    }
    if (partsBrand && Array.isArray(partsBrand)) {
        partsBrand.forEach((item) => queryParams.append('partsBrand', item));
    }
    if (supplier && Array.isArray(supplier)) {
        supplier.forEach((item) => queryParams.append('supplier', item));
    }
    if (tyreDimension && Array.isArray(tyreDimension)) {
        tyreDimension.forEach((item) => queryParams.append('tyreDimension', item));
    }
    if (tyreModel && Array.isArray(tyreModel)) {
        tyreModel.forEach((item) => queryParams.append('tyreModel', item));
    }

    const queryString = queryParams.toString();
    const url = queryString ? `${INVENTORY_ROUTES.GET_INVENTORY_OPTIMIZED}?${queryString}` : INVENTORY_ROUTES.GET_INVENTORY_OPTIMIZED;

    const response = await instance.get(url);
    return response.data;
};

/**
 * Get inventory batch details
 * This API fetches detailed information about a specific inventory batch
 * @param {string} batchNumber - Batch number to get details for
 * @returns {Promise} - Batch details including quantity, expiry, etc.
 */
export const getInventoryBatchDetailApi = async (batchNumber) => {
    const response = await instance.get(INVENTORY_ROUTES.GET_BATCH_DETAILS(batchNumber));
    return response.data.data || response.data;
};

/**
 * Get live stock per batch for one or more inventory parts
 * The backend reads repeated `inventory` params as a list (a comma-joined value is taken as one id).
 * @param {string[]} inventoryIds - Inventory ids to get the batches of
 * @returns {Promise<Array>} - One row per batch: batchNumber, inStock, isTracked, inventory
 */
export const getInventoryBatchStockApi = async (inventoryIds = []) => {
    const queryParams = new URLSearchParams({ groupByBatchNumber: 'true' });
    inventoryIds.forEach((inventoryId) => queryParams.append('inventory', inventoryId));
    const response = await instance.get(`${INVENTORY_ROUTES.GET_INVENTORY_ITEMS}?${queryParams.toString()}`);
    return response.data?.data || [];
};

/**
 * Update inventory item
 * This API updates an inventory item with new details
 * @param {string} inventoryId - ID of the inventory item to update
 * @param {string} batchNumber - Batch number (optional)
 * @param {object} payload - Update data
 * @returns {Promise} - Updated inventory item data
 */
export const updateInventoryApi = async (inventoryId, batchNumber, payload) => {
    let url = INVENTORY_ROUTES.UPDATE_INVENTORY_ITEM(inventoryId, batchNumber);
    const response = await instance.put(url, payload);
    return response.data;
};
